import {
  Directive,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  NgZone,
  Input
} from '@angular/core';

@Directive({
  selector: 'p-paginator, [pPaginatorA11y]'
})
export class PaginatorA11yDirective implements AfterViewInit, OnDestroy {
  @Input() firstPageLabel: string = 'First page';
  @Input() prevPageLabel: string = 'Previous page';
  @Input() nextPageLabel: string = 'Next page';
  @Input() lastPageLabel: string = 'Last page';
  @Input() paginatorAriaLabel: string = 'Pagination';

  private observer: MutationObserver | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.applyA11y();

      if (typeof MutationObserver !== 'undefined') {
        this.observer = new MutationObserver(() => {
          this.applyA11y();
        });

        this.observer.observe(this.el.nativeElement, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'disabled']
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private applyA11y(): void {
    const host = this.el.nativeElement;
    if (!host) {
      return;
    }

    // Ensure paginator container has navigation role and accessible name
    const paginatorContainer = host.matches('.p-paginator')
      ? host
      : host.querySelector<HTMLElement>('.p-paginator');

    if (paginatorContainer) {
      this.setAttrIfNot(paginatorContainer, 'role', 'navigation');
      this.setAttrIfNot(paginatorContainer, 'aria-label', this.paginatorAriaLabel);
    }

    // First page button
    const firstBtn = host.querySelector<HTMLButtonElement>('.p-paginator-first');
    this.setAttrIfNot(firstBtn, 'aria-label', this.firstPageLabel);

    // Previous page button
    const prevBtn = host.querySelector<HTMLButtonElement>('.p-paginator-prev');
    this.setAttrIfNot(prevBtn, 'aria-label', this.prevPageLabel);

    // Next page button
    const nextBtn = host.querySelector<HTMLButtonElement>('.p-paginator-next');
    this.setAttrIfNot(nextBtn, 'aria-label', this.nextPageLabel);

    // Last page button
    const lastBtn = host.querySelector<HTMLButtonElement>('.p-paginator-last');
    this.setAttrIfNot(lastBtn, 'aria-label', this.lastPageLabel);

    // Ensure icon elements inside buttons are hidden from screen readers
    const icons = host.querySelectorAll<HTMLElement>('.p-paginator-icon, .pi');
    icons.forEach(icon => {
      this.setAttrIfNot(icon, 'aria-hidden', 'true');
    });

    // Page number buttons
    const pageButtons = host.querySelectorAll<HTMLButtonElement>('.p-paginator-page');
    pageButtons.forEach(btn => {
      const pageText = (btn.textContent || '').trim();
      const isCurrent = btn.classList.contains('p-highlight');

      if (pageText) {
        const label = isCurrent
          ? `Current page, page ${pageText}`
          : `Page ${pageText}`;
        this.setAttrIfNot(btn, 'aria-label', label);
        if (isCurrent) {
          this.setAttrIfNot(btn, 'aria-current', 'page');
        } else if (btn.hasAttribute('aria-current')) {
          btn.removeAttribute('aria-current');
        }
      }
    });

    // Dropdown triggers if rows per page or jump to page options are present
    const rppDropdown = host.querySelector<HTMLElement>('.p-paginator-rpp-options');
    if (rppDropdown) {
      const trigger = rppDropdown.querySelector<HTMLElement>('.p-dropdown-trigger, input, [tabindex]');
      this.setAttrIfNot(trigger, 'aria-label', 'Rows per page');
    }

    const pageDropdown = host.querySelector<HTMLElement>('.p-paginator-page-options');
    if (pageDropdown) {
      const trigger = pageDropdown.querySelector<HTMLElement>('.p-dropdown-trigger, input, [tabindex]');
      this.setAttrIfNot(trigger, 'aria-label', 'Jump to page');
    }
  }

  private setAttrIfNot(element: Element | null, attr: string, value: string): void {
    if (element && element.getAttribute(attr) !== value) {
      element.setAttribute(attr, value);
    }
  }
}
